import { useEffect, useRef, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';

import confirmingOrderIcon from '@/assets/order/confirming-order.png';
import { Card } from '@/components/ui/card.tsx';
import { usePrefersReducedMotion } from '@/hooks/use-prefers-reduced-motion.ts';
import { cn } from '@/lib/utils.ts';

const DEFAULT_STEPS = [
  'Reserving your appointment time',
  'Verifying your order',
  'Submitting your order',
];

// Tuned just under p95 latency (~4.75s) so the response usually lands mid-fill.
const TOTAL_ANIMATION_MS = 4000;
const INITIAL_REVEAL_MS = 100;
const FINAL_HOLD_MS = 1000;

const RING_SIZE = 112;
const RING_STROKE = 4;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

// Fill held while the last step loads; a smaller final section completes on resolve.
const HELD_RING_PROGRESS = 0.82;

type Props = {
  requestComplete?: boolean;
  onComplete?: () => void;
  steps?: string[];
  className?: string;
};

export default function ConfirmingOrderLoader({
  requestComplete = false,
  onComplete,
  steps = DEFAULT_STEPS,
  className,
}: Props) {
  const reducedMotion = usePrefersReducedMotion();
  const total = steps.length;

  const stepDurationMs = (TOTAL_ANIMATION_MS - INITIAL_REVEAL_MS) / Math.max(total, 1);
  const finishStepMs = Math.round(stepDurationMs / 6);
  const ringSettleMs = Math.round(stepDurationMs / 4);

  // ringFill spans 0..total+1; the final section and last check are held until the request resolves.
  const [revealed, setRevealed] = useState(reducedMotion ? total : 0);
  const [completed, setCompleted] = useState(reducedMotion ? total : 0);
  const [ringFill, setRingFill] = useState(reducedMotion ? total + 1 : 0);
  const [finishing, setFinishing] = useState(false);

  const completedRef = useRef(completed);
  const onCompleteRef = useRef(onComplete);
  const nominalTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const finishTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const finishStartedRef = useRef(false);

  useEffect(() => {
    completedRef.current = completed;
  }, [completed]);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Steady playback: reveal each step and fill its ring section as it starts loading.
  useEffect(() => {
    if (reducedMotion) return;

    const timers: ReturnType<typeof setTimeout>[] = [];
    for (let i = 0; i < total; i++) {
      const start = INITIAL_REVEAL_MS + i * stepDurationMs;
      timers.push(setTimeout(() => setRevealed((r) => Math.max(r, i + 1)), start));
      timers.push(setTimeout(() => setRingFill((r) => Math.max(r, i + 1)), start));
      if (i === total - 1) continue;
      timers.push(
        setTimeout(() => setCompleted((c) => Math.max(c, i + 1)), start + stepDurationMs)
      );
    }
    nominalTimersRef.current = timers;
  }, [reducedMotion, total, stepDurationMs]);

  // On resolve: finish remaining steps (fast if it beat the animation), hold, then hand off. Runs once.
  useEffect(() => {
    if (!requestComplete || finishStartedRef.current) return;
    finishStartedRef.current = true;

    if (reducedMotion) {
      finishTimersRef.current = [setTimeout(() => onCompleteRef.current?.(), FINAL_HOLD_MS)];
      return;
    }

    nominalTimersRef.current.forEach(clearTimeout);
    nominalTimersRef.current = [];
    setFinishing(true);
    setRevealed((r) => Math.max(r, total));

    const startFrom = completedRef.current;
    const remaining = total - startFrom;
    const timers: ReturnType<typeof setTimeout>[] = [];
    for (let step = startFrom + 1; step <= total; step++) {
      const order = step - startFrom;
      // Last step fills the final section; earlier steps just catch up to their own.
      const fill = step === total ? total + 1 : step;
      timers.push(
        setTimeout(() => {
          setCompleted((c) => Math.max(c, step));
          setRingFill((r) => Math.max(r, fill));
        }, order * finishStepMs)
      );
    }
    timers.push(
      setTimeout(() => onCompleteRef.current?.(), remaining * finishStepMs + FINAL_HOLD_MS)
    );
    finishTimersRef.current = timers;
  }, [requestComplete, reducedMotion, total, finishStepMs]);

  useEffect(
    () => () => {
      nominalTimersRef.current.forEach(clearTimeout);
      finishTimersRef.current.forEach(clearTimeout);
    },
    []
  );

  // Clamp at 1 — overshooting makes the SVG dash wrap and erase the stroke.
  const rawProgress = total > 0 ? (ringFill / total) * HELD_RING_PROGRESS : 0;
  const ringOffset = RING_CIRCUMFERENCE * (1 - Math.min(rawProgress, 1));

  return (
    <Card className={cn('items-center gap-7 text-center', className)}>
      <div
        className="relative flex items-center justify-center"
        style={{ width: RING_SIZE, height: RING_SIZE }}
      >
        <svg
          className="absolute inset-0 -rotate-90"
          width={RING_SIZE}
          height={RING_SIZE}
          viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
          aria-hidden
        >
          <circle
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={RING_RADIUS}
            fill="none"
            stroke="var(--color-hydrangea-500)"
            strokeWidth={RING_STROKE}
            strokeLinecap="round"
            strokeDasharray={RING_CIRCUMFERENCE}
            strokeDashoffset={ringOffset}
            style={{
              transition: reducedMotion
                ? undefined
                : `stroke-dashoffset ${finishing ? ringSettleMs : stepDurationMs}ms ease-out`,
            }}
          />
        </svg>
        <img src={confirmingOrderIcon} alt="" className="size-[78px] object-contain" />
      </div>

      <div className="flex flex-col items-center gap-2">
        <h1 className="font-martina text-[32px] leading-[115%] font-normal tracking-tight text-charcoal-900">
          Confirming your order
        </h1>
        <p className="font-circular text-base leading-[140%] text-charcoal-500">
          Hold tight, this may take a second.
        </p>
      </div>

      <ol className="flex w-full max-w-sm flex-col gap-3 text-left" aria-live="polite">
        {steps.map((label, index) => {
          const isVisible = index < revealed;
          const isDone = index < completed;
          const isActive = isVisible && !isDone;

          return (
            <li
              key={label}
              className={cn(
                'flex h-14 items-center gap-4 rounded-xl border border-neutral-300 bg-cream px-7',
                'transition-all duration-500 ease-out motion-reduce:transition-none',
                isVisible
                  ? 'translate-y-0 opacity-100'
                  : 'pointer-events-none translate-y-3 opacity-0'
              )}
            >
              <span className="relative flex size-6 shrink-0 items-center justify-center">
                <Loader2
                  className={cn(
                    'absolute size-5 animate-spin text-hydrangea-500/50 transition-opacity duration-300',
                    isActive ? 'opacity-100' : 'opacity-0'
                  )}
                  aria-hidden
                />
                <Check
                  className={cn(
                    'absolute size-6 text-hydrangea-500 transition-all duration-300',
                    isDone ? 'scale-100 opacity-100' : 'scale-50 opacity-0'
                  )}
                  aria-hidden
                />
              </span>
              <span
                className={cn(
                  'font-circular text-base leading-[140%] transition-colors duration-300',
                  isDone ? 'text-charcoal-900' : 'text-charcoal-400'
                )}
              >
                {label}
              </span>
            </li>
          );
        })}
      </ol>
    </Card>
  );
}
